"use client";

import { useGetCoatings, useGetProducts } from "@/hooks/main-hooks";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Layers, Palette } from "lucide-react";
import { useLocale } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect } from "react";

// ── Card artwork (ui/public/products/) ───────────────────────────────────────
const PROFILES_IMAGE = "/products/profiles-card.jpg";
const COATINGS_IMAGE = "/products/coatings-card.jpg";

export default function ProductsPage() {
  const locale = useLocale();
  const isAr = locale === "ar";
  const router = useRouter();

  // Backwards-compat: old deep link ?section=coatings → dedicated page.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("section") === "coatings") {
      router.replace(`/${locale}/products/coatings`);
    }
  }, [router, locale]);

  const { data: productsData } = useGetProducts({ page: 1, limit: 1 });
  const { data: coatingsData } = useGetCoatings({ page: 1, limit: 1 });
  const productsCount = productsData?.totalProducts ?? null;
  const coatingsCount = coatingsData?.totalCoatings ?? null;

  const Arrow = isAr ? ArrowLeft : ArrowRight;

  const cards = [
    {
      href: `/${locale}/products/profiles`,
      image: PROFILES_IMAGE,
      icon: Layers,
      eyebrowAr: "أنظمة الألومنيوم",
      eyebrowEn: "Aluminium Systems",
      titleAr: "القطاعات",
      titleEn: "Profiles",
      descAr:
        "قطاعات ألومنيوم هندسية للنوافذ والأبواب والواجهات والدرابزينات — مصمّمة للأداء الإنشائي والحراري.",
      descEn:
        "Engineered aluminium profiles for windows, doors, façades and railings — built for structural and thermal performance.",
      count: productsCount,
      countLabelAr: "نظام",
      countLabelEn: "systems",
      accent: "from-brand-navy/95 via-brand-navy/60",
    },
    {
      href: `/${locale}/products/coatings`,
      image: COATINGS_IMAGE,
      icon: Palette,
      eyebrowAr: "الألوان والتشطيبات",
      eyebrowEn: "Colours & Finishes",
      titleAr: "الدهانات",
      titleEn: "Coatings",
      descAr:
        "كارتيلات الألوان لطلاء البودرة، تأثير الخشب، والأنودة — تشطيبات متينة بمعايير عالمية.",
      descEn:
        "Colour catalogues for powder coating, wood effect and anodizing — durable finishes to international standards.",
      count: coatingsCount,
      countLabelAr: "كارتيلة",
      countLabelEn: "catalogues",
      accent: "from-[#3a1010]/95 via-brand-red/50",
    },
  ];

  return (
    <main
      dir={isAr ? "rtl" : "ltr"}
      className={`min-h-[70vh] bg-brand-creamy pb-24 ${isAr ? "font-almarai" : ""}`}
    >
      <div className="container mx-auto px-6 pt-16">
        {/* Intro */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mb-12"
        >
          <p className="text-xs font-black tracking-[0.35em] uppercase text-brand-red mb-3">
            {isAr ? "استكشف منتجاتنا" : "Explore our range"}
          </p>
          <h2 className="text-3xl md:text-5xl font-black text-brand-navy tracking-tight leading-[1.05]">
            {isAr ? "اختر ما تبحث عنه" : "Choose what you're looking for"}
          </h2>
          <p className="mt-4 text-brand-navy/60 leading-relaxed">
            {isAr
              ? "قسمان متكاملان: أنظمة القطاعات الهندسية، وكارتيلات الدهانات والتشطيبات."
              : "Two complementary sections: engineered profile systems, and coating & finish catalogues."}
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {cards.map((c, i) => (
            <motion.div
              key={c.href}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
            >
              <Link
                href={c.href}
                className="group relative block h-[440px] md:h-[520px] rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-20px_rgba(27,43,94,0.35)]"
              >
                <Image
                  src={c.image}
                  alt={isAr ? c.titleAr : c.titleEn}
                  fill
                  sizes="(max-width: 768px) 100vw, 50vw"
                  className="object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110"
                  priority={i === 0}
                />
                {/* Overlays */}
                <div className={`absolute inset-0 bg-linear-to-t ${c.accent} to-transparent`} />
                <div className="absolute inset-0 bg-brand-navy/10 group-hover:bg-transparent transition-colors duration-700" />

                {/* Top-left icon */}
                <div className="absolute top-6 start-6 w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-md border border-white/25 flex items-center justify-center text-white">
                  <c.icon size={24} />
                </div>

                {/* Count pill */}
                {c.count !== null && (
                  <div className="absolute top-6 end-6 px-4 py-2 rounded-full bg-white/95 backdrop-blur text-brand-navy text-xs font-black tracking-wider shadow">
                    {c.count}{" "}
                    <span className="font-semibold text-brand-navy/60">
                      {isAr ? c.countLabelAr : c.countLabelEn}
                    </span>
                  </div>
                )}

                {/* Content */}
                <div className="absolute inset-x-0 bottom-0 p-8 md:p-10 text-white">
                  <p className="text-[11px] font-black tracking-[0.35em] uppercase text-white/70 mb-3">
                    {isAr ? c.eyebrowAr : c.eyebrowEn}
                  </p>
                  <h3 className="text-4xl md:text-5xl font-black tracking-tight leading-none mb-4">
                    {isAr ? c.titleAr : c.titleEn}
                  </h3>
                  <p className="text-white/80 max-w-md leading-relaxed text-sm md:text-base mb-7">
                    {isAr ? c.descAr : c.descEn}
                  </p>
                  <span className="inline-flex items-center gap-3 px-6 py-3.5 rounded-full bg-white text-brand-navy text-xs font-black uppercase tracking-[0.2em] shadow-lg transition-all duration-300 group-hover:bg-brand-red group-hover:text-white group-hover:gap-4">
                    {isAr ? "استكشف" : "Explore"}
                    <Arrow size={16} />
                  </span>
                </div>

                {/* Corner accent line */}
                <div className="absolute bottom-0 inset-x-0 h-1 bg-linear-to-r from-brand-red via-white/60 to-transparent scale-x-0 group-hover:scale-x-100 origin-left transition-transform duration-700" />
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </main>
  );
}
